import React, { useState } from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  useDisclosure,
  UnorderedList,
  ListItem,
  Link,
  Icon
} from '@chakra-ui/react';
import { InfoOutlineIcon, ArrowBackIcon } from '@chakra-ui/icons';

const HelpModalVis = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [section, setSection] = useState('main');

  const closeAndReset = () => {
    onClose();
    setSection('main');
  };

  const renderContent = () => {
    switch (section) {
        case 'dpll':
            return (
              <>
                <ModalHeader>DPLL algoritmus</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Text mb={3}>
                    DPLL algoritmus očakáva na vstupe množinu klauzúl a množinu interpretácií výrokových premenných.
                  </Text>
                  <UnorderedList spacing={2}>
                    <ListItem>
                      V prvom kroku zjednoduší formulu prostredníctvom jednotkovej propagácie.
                    </ListItem>
                    <ListItem>
                      V druhom kroku pokračuje zjednodušovaním formuly prostredníctvom eliminácie čistého literálu.
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Kontrola po týchto krokoch:</Text>
                      <UnorderedList mt={1} ml={4}>
                        <ListItem>Ak je množina klauzúl prázdna → algoritmus vráti interpretáciu (SAT).</ListItem>
                        <ListItem>Ak obsahuje prázdnu klauzulu → formula je nesplniteľná (UNSAT).</ListItem>
                      </UnorderedList>
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Vetvenie podľa výrokovej premennej <i>p</i>:</Text>
                      <UnorderedList mt={1} ml={4}>
                        <ListItem>
                          Priradí sa <i>p=true</i>, spustí sa rekurzívne DPLL. Ak vráti interpretáciu → hotovo.
                        </ListItem>
                        <ListItem>
                          Inak sa priradí <i>p=false</i> a spustí sa rekurzia znovu.
                        </ListItem>
                      </UnorderedList>
                    </ListItem>
                  </UnorderedList>
                  <Text mt={4}>
                    Ak žiadna vetva nevedie k splnenej interpretácii, formula je nesplniteľná a algoritmus vráti UNSAT.
                  </Text>
                </ModalBody>
                <ModalFooter>
                  <Button leftIcon={<ArrowBackIcon />} onClick={() => setSection('main')}>
                    Naspäť
                  </Button>
                </ModalFooter>
              </>
            );
          case 'pure':
                return (
                  <>
                    <ModalHeader>Čistý literál</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                      <Text mb={3}>
                        Čistý literál je taký literál, ktorý sa v celej formuli vyskytuje iba v jednej polarite –
                        buď len pozitívne, alebo len negatívne.
                      </Text>
                      <Text mb={3}>
                        Napríklad, ak sa premenná <code>a</code> vyskytuje iba ako <code>a</code> (nikdy ako <code>¬a</code>),
                        tak <code>a</code> je čistý literál.
                      </Text>
                      <Text mb={3}>
                        V DPLL algoritme je eliminácia čistých literálov optimalizačný krok:
                      </Text>
                      <UnorderedList spacing={2}>
                        <ListItem>
                          Ak sa premenná vyskytuje len ako <code>a</code>, môžeme ju priradiť na <strong>true</strong>.
                        </ListItem>
                        <ListItem>
                          Ak sa premenná vyskytuje len ako <code>¬a</code>, môžeme ju priradiť na <strong>false</strong>.
                        </ListItem>
                        <ListItem>
                          Po priradení sa odstránia všetky klauzuly, kde sa čistý literál nachádza, čím sa formula zjednoduší.
                        </ListItem>
                      </UnorderedList>
                      <Text mt={4}>
                        Tento krok nie je nevyhnutný pre korektnosť DPLL, ale výrazne znižuje počet potrebných rozhodnutí.
                      </Text>
                    </ModalBody>
                    <ModalFooter>
                      <Button leftIcon={<ArrowBackIcon />} onClick={() => setSection('main')}>
                        Naspäť
                      </Button>
                    </ModalFooter>
                  </>
                );
              
      case 'unit':
        return (
          <>
            <ModalHeader>Jednotková propagácia</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={3}>
                Ak existuje klauzula, v ktorej zostáva len jeden nepriradený literál, jeho hodnota sa musí priradiť
                tak, aby bola klauzula pravdivá. Tento krok sa volá jednotková propagácia.
              </Text>
              <Text>
                Príklad: Klauzula (¬x ∨ y) má už x=false, takže y musí byť true, aby bola klauzula splnená.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button leftIcon={<ArrowBackIcon />} onClick={() => setSection('main')}>
                Naspäť
              </Button>
            </ModalFooter>
          </>
        );
      case 'backtrack':
        return (
          <>
            <ModalHeader>Backtrack</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>
                Ak sa algoritmus dostane do bodu, kde nie je možné uspokojiť niektorú z klauzúl, vráti sa späť na
                predchádzajúce rozhodnutie a skúsi inú hodnotu. Tento proces sa nazýva backtracking.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button leftIcon={<ArrowBackIcon />} onClick={() => setSection('main')}>
                Naspäť
              </Button>
            </ModalFooter>
          </>
        );
      case 'conflict':
        return (
          <>
            <ModalHeader>Konflikt</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>
                Konflikt znamená, že aspoň jedna klauzula je nepravdivá vzhľadom na aktuálne priradenia premenných.
                To znamená, že cesta, ktorou sa DPLL vydal, nevedie k riešeniu.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button leftIcon={<ArrowBackIcon />} onClick={() => setSection('main')}>
                Naspäť
              </Button>
            </ModalFooter>
          </>
        );
      case 'sat':
        return (
          <>
            <ModalHeader>Splniteľnosť (SAT / UNSAT)</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>
                Ak existuje aspoň jedno priradenie, ktoré robí všetky klauzuly pravdivé, formula je SAT. Ak také
                priradenie neexistuje, formula je UNSAT.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button leftIcon={<ArrowBackIcon />} onClick={() => setSection('main')}>
                Naspäť
              </Button>
            </ModalFooter>
          </>
        );
      default:
        return (
          <>
            <ModalHeader>Sprievodca vizualizáciou DPLL</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={3}>
                Vizualizácia zobrazuje rozhodovací strom vytvorený DPLL algoritmom. Tu sú základné pojmy:
              </Text>
              <UnorderedList spacing={3}>
              <ListItem>
                <strong>DPLL algoritmus</strong> –{' '}
                <Link color="blue.500" onClick={() => setSection('dpll')}>
                    Prejsť na vysvetlenie
                </Link>
                </ListItem>
                <ListItem>
                  <strong>Jednotková propagácia</strong> –{' '}
                  <Link color="blue.500" onClick={() => setSection('unit')}>
                    Prejsť na vysvetlenie
                  </Link>
                </ListItem>
                <ListItem>
                    <strong>Čistý literál</strong> –{' '}
                    <Link color="blue.500" onClick={() => setSection('pure')}>
                    Prejsť na vysvetlenie
                    </Link>
                </ListItem>

                <ListItem>
                  <strong>Backtrack</strong> –{' '}
                  <Link color="blue.500" onClick={() => setSection('backtrack')}>
                    Prejsť na vysvetlenie
                  </Link>
                </ListItem>
                <ListItem>
                  <strong>Konflikt</strong> –{' '}
                  <Link color="blue.500" onClick={() => setSection('conflict')}>
                    Prejsť na vysvetlenie
                  </Link>
                </ListItem>
                <ListItem>
                  <strong>Splniteľnosť (SAT / UNSAT)</strong> –{' '}
                  <Link color="blue.500" onClick={() => setSection('sat')}>
                    Prejsť na vysvetlenie
                  </Link>
                </ListItem>
              </UnorderedList>
            </ModalBody>
            <ModalFooter>
              <Button onClick={closeAndReset} colorScheme="blue">
                Zatvoriť
              </Button>
            </ModalFooter>
          </>
        );
    }
  };

  return (
    <>
      <Button onClick={onOpen} variant="ghost" size="sm" ml="auto" leftIcon={<InfoOutlineIcon />}>
        Sprievodca vizualizáciou
      </Button>

      <Modal isOpen={isOpen} onClose={closeAndReset} size="lg">
        <ModalOverlay />
        <ModalContent>{renderContent()}</ModalContent>
      </Modal>
    </>
  );
};

export default HelpModalVis;
